import { useId } from 'react'

import type { Activity, ActivityLibrary, ExperimentPlan } from '../../lib/experiments/api'
import type { Step, StepState } from '../../lib/capture/guidance'
import { formatMinutes } from '../../lib/format'
import { EXPERIMENT_ACTIVITY_COPY, EXPERIMENT_NOTES, EXPERIMENT_WORDS } from '../record/phaseWords'
import { Button, Card, Pill } from '../ui'
import { ChoiceCards, type ChoiceOption } from './ChoiceCards'
import { StepRail } from './StepRail'
import '../../styles/experiment-flow.css'

/** The library owns the names, order, timing and the exploratory limit. */
export function ActivityChoices({
  library,
  value,
  onChange,
}: {
  library: ActivityLibrary
  value: Activity['key']
  onChange: (value: Activity['key']) => void
}) {
  const labelId = useId()
  const custom = library.activities.find((activity) => activity.key === 'custom')
  const options: ChoiceOption<Activity['key']>[] = library.activities
    .filter((activity) => activity.key !== 'custom')
    .map((activity) => ({
      value: activity.key,
      title: activity.name,
      description: activity.limit_text ?? (activity.key in EXPERIMENT_ACTIVITY_COPY
        ? EXPERIMENT_ACTIVITY_COPY[activity.key as keyof typeof EXPERIMENT_ACTIVITY_COPY]
        : undefined),
      meta: activity.duration_s === null
        ? `${EXPERIMENT_WORDS.settle} ${formatMinutes(activity.settle_s)}`
        : `${formatMinutes(activity.duration_s)} · ${EXPERIMENT_WORDS.settle.toLowerCase()} ${formatMinutes(activity.settle_s)}`,
      children: activity.exploratory
        ? <Pill tone="amber" dot className="experiment-flow__warning">{EXPERIMENT_WORDS.exploratory}</Pill>
        : undefined,
    }))

  return (
    <section className="experiment-flow__activities" aria-labelledby={labelId}>
      <h2 id={labelId} className="experiment-flow__label">{EXPERIMENT_WORDS.oneActivity}</h2>
      <ChoiceCards options={options} value={value} onChange={onChange} label={EXPERIMENT_WORDS.oneActivity} pressed />
      {custom && (
        <div className="experiment-flow__custom-choice">
          <Button
            variant="secondary"
            aria-pressed={value === custom.key}
            onClick={() => onChange(custom.key)}
          >
            {custom.name}…
          </Button>
          <p>{EXPERIMENT_WORDS.customHelp}</p>
        </div>
      )}
    </section>
  )
}

type ProtocolStage = 'before' | 'activity' | 'settle' | 'after' | 'report' | 'result'

const STEP_STATES: Record<ProtocolStage, readonly StepState[]> = {
  before: ['now', 'pending', 'pending', 'pending', 'pending', 'pending'],
  activity: ['done', 'now', 'pending', 'pending', 'pending', 'pending'],
  settle: ['done', 'done', 'now', 'pending', 'pending', 'pending'],
  after: ['done', 'done', 'done', 'now', 'pending', 'pending'],
  report: ['done', 'done', 'done', 'done', 'now', 'pending'],
  result: ['done', 'done', 'done', 'done', 'done', 'now'],
}

/** No protocol length is supplied locally: the caller passes its persisted plan. */
export function ExperimentProtocol({
  plan,
  activity,
  current = 'before',
  notes = true,
}: {
  plan: Pick<ExperimentPlan, 'reading_s' | 'settle_s' | 'custom_name'>
  activity: Activity
  current?: ProtocolStage
  notes?: boolean
}) {
  const titleId = useId()
  const reading = `${formatMinutes(plan.reading_s)} ${EXPERIMENT_WORDS.reading}`
  const name = plan.custom_name ?? activity.name
  const labels = [
    `${EXPERIMENT_WORDS.before} · ${reading}`,
    activity.duration_s === null ? name : `${name} · ${formatMinutes(activity.duration_s)}`,
    `${EXPERIMENT_WORDS.settle} · ${formatMinutes(plan.settle_s)}`,
    `${EXPERIMENT_WORDS.after} · ${reading}`,
    EXPERIMENT_WORDS.report,
    EXPERIMENT_WORDS.result,
  ]
  const numbers = ['1', '2', '3', '4', '5', '6']
  const steps: Step[] = labels.map((label, index) => ({
    label,
    n: numbers[index],
    state: STEP_STATES[current][index],
  }))

  return (
    <Card as="section" className="experiment-flow__protocol" aria-labelledby={titleId}>
      <h2 id={titleId} className="experiment-flow__label">{EXPERIMENT_WORDS.protocol}</h2>
      <StepRail steps={steps} />
      {notes && (
        <div className="experiment-flow__notes">
          {EXPERIMENT_NOTES.map((note) => (
            <div key={note.title}>
              <h3>{note.title}</h3>
              <p>{note.body}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
